/*
 *
 *  * Copyright(c) 2021 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import * as _ from 'underscore';
import { Injectable } from '@angular/core';
import { AppConfigService } from '@app/common/app-config.service';
import { Count } from '@core/lib/components/to-do-count/count';
import { DataAccessService } from '@app/common/data-access.service';
import { Logger } from '@core/lib/services';
import { appConstants } from '@app/common/app-constants';
import { MessageService } from '@core/lib/services/message.service';
import { Events, EventService } from '@app/common/event';
import { ToDoList, ToDoListId } from '@app/data-model/list/to-do-list';
import { BehaviorSubject } from 'rxjs';
import { REPORT_STATUS } from '@app/di/integrations/reporting/reporting-contants';

const log = new Logger('ToDoCountService');

@Injectable({
  providedIn: 'root'
})
export class ToDoCountService {
  private list: ToDoList[];
  private assignedCount: Count;
  private unsignedCount: Count;
  private nawCoreportCount: Count;
  private nawReviewRequestedCount: Count;

  private workItemStateWorklistId;
  private reportStateWorklistId;
  private attendingReadWorklistId;
  private nawCoreportWorklistId;
  private nawReviewRequestedWorklistId;
  private assignedUser;

  private assignedCountObserver$: BehaviorSubject<Count> = new BehaviorSubject<Count>(undefined);

  constructor(
    private applicationConfigurationService: AppConfigService,
    private dataAccess: DataAccessService,
    private messageService: MessageService,
    private eventService: EventService
  ) {
    this.registerEvents();
    this.applicationConfigurationService.cacheUnsignedWLTimeDepth();
  }

  getCounter() {
    return [this.assignedCount, this.unsignedCount, this.nawCoreportCount, this.nawReviewRequestedCount]
      .filter(Boolean)
      .reduce(
        (prev, current) =>
          new Count(prev.total + current.total, prev.stat + current.stat, prev.findings + current.findings),
        new Count(0, 0, 0)
      );
  }

  getAssignedCount() {
    return this.list && this.assignedCount;
  }

  getAssignedCountObserver() {
    return this.assignedCountObserver$.asObservable();
  }

  setWorkItemStateWorklistId(value) {
    this.workItemStateWorklistId = value;
  }

  setReportStateWorklistId(value) {
    this.reportStateWorklistId = value;
  }

  setAttendingReadWorklistId(value) {
    this.attendingReadWorklistId = value;
  }

  setNawCoreportWorklistId(value) {
    this.nawCoreportWorklistId = value;
  }

  setNawReviewRequestedWorklistId(value) {
    this.nawReviewRequestedWorklistId = value;
  }

  setList(value: ToDoList[]) {
    this.list = value;
    this.tryToGetCountUpdate();
  }

  updateTodoListCount() {
    this.tryToGetCountUpdate();
  }

  private doUpdate(updates) {
    // _.each(updates, this.list.update, this.list);
    _.each(updates, item => {
      const list: ToDoList = _.find(this.list, { id: item.id });
      if (list) {
        list.count = new Count(item.total, item.stat, item.findings);

        if (list.id === ToDoListId.ASSIGNED) {
          if (list.count.total !== this.assignedCount?.total) {
            this.assignedCountObserver$.next(list.count);
          }
          this.assignedCount = list.count;
        }
        if (list.id === ToDoListId.UNSIGNED) {
          this.unsignedCount = list.count;
        }
        if (list.id === ToDoListId.NAW_COREPORT) {
          this.nawCoreportCount = list.count;
        }
        if (list.id === ToDoListId.NAW_REVIEWREQUESTED) {
          this.nawReviewRequestedCount = list.count;
        }
      }
    });
    if (updates.length) {
      this.messageService.broadcast('list.count.update');
    }
  }

  private calculateLocalToDoTotals(localToDoData) {
    this.dataAccess.setLocalToDoData(localToDoData);
    this.doUpdate(this.dataAccess.calculateToDoTotals(localToDoData));
  }

  private updateLocalCount(localToDoData, state, target) {
    log.debug('performing ToDoCount - updateLocalCount on :', target);
    const list = [];
    let timeOfExam, procedureStatusId;
    if (target.keys) {
      timeOfExam = target.keys.timeOfExam;
      log.debug('time Of the Exam is:', timeOfExam);
      procedureStatusId = target.keys.procedureStatusId;
      log.debug('procedureStatusId of the exam is :', procedureStatusId);
    }
    //Handling Primary
    if (state.workitemId !== -1) {
      list.push({
        procedureId: state.workitemId,
        procedurePriority: state.workitemPriority,
        procedurePriorityId: state.workitemPriorityId,
        aiFinding: state.aiFinding,
        timeOfExam: timeOfExam,
        procedureStatusId: procedureStatusId
      });
    }

    //Handling Association List
    const associationList = target.associationList;
    log.debug('Association List :', associationList);
    if (Array.isArray(associationList) && associationList.length > 0) {
      _.each(associationList, id => {
        list.push({
          procedureId: id,
          procedurePriority: state.workitemPriority,
          procedurePriorityId: state.workitemPriorityId,
          aiFinding: state.aiFinding,
          timeOfExam: timeOfExam,
          procedureStatusId: procedureStatusId
        });
      });
    }

    let result = false;
    const action = state.request.actionCode;
    _.each(list, currentItem => {
      const indexOfCurrentItem = localToDoData.assignedItems
        .map(e => {
          return e.procedureId;
        })
        .indexOf(currentItem.procedureId);

      if (action === 'ASSIGN' || action === 'READ') {
        //add the exam that just had the state change to our local To Do data used for calculation.
        if (
          indexOfCurrentItem === -1 &&
          this.assignedUser !== undefined &&
          this.applicationConfigurationService.getUserId() ===
            state.ownership.assignedToUser[this.assignedUser].userName
        ) {
          this.updateLocalToDoCounts(indexOfCurrentItem, currentItem, localToDoData, false);
        }
        if (
          indexOfCurrentItem !== -1 &&
          this.assignedUser !== undefined &&
          this.applicationConfigurationService.getUserId() !==
            state.ownership.assignedToUser[this.assignedUser].userName
        ) {
          this.updateLocalToDoCounts(indexOfCurrentItem, currentItem, localToDoData, true);
        }
        result = true;
      }
      if (
        state.extAppWorkItemState === 'WFM_Default_State' &&
        _.contains(['UNASSIGN', 'READ_CLOSE_AUTOADVANCE_UNASSIGN', 'READ_CLOSE'], action)
      ) {
        //remove the exam that just had the state change from our local To Do data used for calculation.
        if (indexOfCurrentItem >= 0) {
          this.updateLocalToDoCounts(indexOfCurrentItem, currentItem, localToDoData, true);
        }
        result = true;
      }
      if (
        (action === Events.close.value || action === Events.savereport.value) &&
        state.extAppWorkItemState === 'WFM_Default_State'
      ) {
        result = this.handleReportStatusUpdate(list, state, currentItem, indexOfCurrentItem, localToDoData);
      }
    });
    return result;
  }

  private handleReportStatusUpdate(list, state, currentItem, indexOfCurrentItem, localToDoData) {
    //draft report is 9000 code and unsigned items will be decreased by one
    if (state.request.reportStatus === 9000) {
      _.each(list, item => {
        const indexOfCurrSavedItem = localToDoData.savedItems
          .map(e => {
            return e.procedureId;
          })
          .indexOf(currentItem.procedureId);
        this.updateLocalToDoCountSavedItem(indexOfCurrSavedItem, item, localToDoData, true);
      });

      for (const item in list) {
        const indexOfNawCoreportItem = localToDoData.nawCoreportItems.findIndex(
          nawItem => nawItem.procedureId === currentItem.procedureId
        );
        if (indexOfNawCoreportItem !== -1) {
          this.updateLocalNawCoreportItems(indexOfNawCoreportItem, item, localToDoData);
        }

        const indexOfReviewRequestedItem = localToDoData.nawReviewRequestedItems.findIndex(
          nawItem => nawItem.procedureId === currentItem.procedureId
        );
        if (indexOfReviewRequestedItem !== -1) {
          this.updateLocalNawReviewRequestedItem(indexOfReviewRequestedItem, item, localToDoData);
        }
      }
    }
    //draft report is 7500 code and unsigned items will be increased by one
    if (state.request.reportStatus === 7500) {
      _.each(list, () => {
        const indexOfCurrSavedItem = localToDoData.savedItems
          .map(e => {
            return e.procedureId;
          })
          .indexOf(currentItem.procedureId);
        //make the update for Todocount count only if the report does not exist in the unsigned List of the user
        const isCountUpdatable = this.isTodoCountUpdatable(currentItem.timeOfExam, currentItem.procedureStatusId);
        if (indexOfCurrSavedItem < 0 && isCountUpdatable) {
          this.updateLocalToDoCountSavedItem(indexOfCurrSavedItem, currentItem, localToDoData, false);
        }
      });
    }
    //remove the exam that just had the state change from our local To Do data used for calculation.
    //If "Exam remains in Verified status in Workflow Manager" Option is checked in AD, updating toDO count is
    //restricted to fix ALM# 14732
    if (
      indexOfCurrentItem >= 0 &&
      this.applicationConfigurationService.getAppConfigByKey('reportingConfig') === 'false'
    ) {
      this.updateLocalToDoCounts(indexOfCurrentItem, currentItem, localToDoData, true);
    }

    return true;
  }

  private isTodoCountUpdatable(timeOfExam, reportStatusId) {
    const reportingConfig = this.isReportingConfig(reportStatusId);
    const days = this.applicationConfigurationService.getUnsignedWLTimeDepth();
    const time = this.dataAccess.getNumberOfDays(timeOfExam);
    const isValidTimeOfexam = days === 0 || (days > 0 && days >= time);
    return isValidTimeOfexam && (reportingConfig || this.isReferenceOnlyToDictated(reportStatusId));
  }

  private isReportingConfig(reportStatusId) {
    const reportingConfig = this.applicationConfigurationService.getAppConfigByKey('reportingConfig') === 'true';
    return reportStatusId !== appConstants.ExamStatus.REFERENCE_ONLY && !reportingConfig;
  }

  private isReferenceOnlyToDictated(reportStatusId) {
    const isReferenceOnly =
      this.applicationConfigurationService.getAppConfigByKey('referenceOnlyToDictated') === 'true';
    return reportStatusId === appConstants.ExamStatus.REFERENCE_ONLY && isReferenceOnly;
  }

  private updateLocalToDoCountSavedItem(indexOfCurrSavedItem, currSavedItem, localToDoData, isRemove) {
    if (isRemove) {
      if (indexOfCurrSavedItem >= 0) {
        localToDoData.savedItems.splice(indexOfCurrSavedItem, 1);
      }
    } else if (indexOfCurrSavedItem < 0) {
      localToDoData.savedItems.push(currSavedItem);
    }
    this.calculateLocalToDoTotals(localToDoData);
  }

  private updateLocalToDoCounts(indexOfCurrentItem, currentItem, localToDoData, isRemove) {
    if (isRemove) {
      if (indexOfCurrentItem >= 0) {
        localToDoData.assignedItems.splice(indexOfCurrentItem, 1);
      }
    } else {
      localToDoData.assignedItems.push(currentItem);
    }
    this.calculateLocalToDoTotals(localToDoData);
  }

  private updateLocalNawReviewRequestedItem(indexOfCurrentItem, currentItem, localToDoData) {
    localToDoData.nawReviewRequestedItems.splice(indexOfCurrentItem, 1);
    this.calculateLocalToDoTotals(localToDoData);
  }

  private updateLocalNawCoreportItems(indexOfCurrentItem, currentItem, localToDoData) {
    localToDoData.nawCoreportItems.splice(indexOfCurrentItem, 1);
    this.calculateLocalToDoTotals(localToDoData);
  }

  //After the first backend call to get To Do Counts, attempt to calculate these totals client side, versus
  //making a backend hit each time a state changes.
  private tryToGetLocalCountUpdate = (state, target) => {
    let countsUpdatedLocally = false;
    const localToDoData = this.dataAccess.getLocalToDoData();

    if (localToDoData !== undefined) {
      countsUpdatedLocally = this.updateLocalCount(localToDoData, state, target);
    }
    return countsUpdatedLocally;
  };

  private tryToGetCountUpdate = _.throttle(() => {
    this.dataAccess
      .getTodoWorklistsCount(
        this.workItemStateWorklistId,
        this.reportStateWorklistId,
        this.attendingReadWorklistId,
        this.nawCoreportWorklistId,
        this.nawReviewRequestedWorklistId
      )
      .then(updates => {
        this.doUpdate(updates);
      });
  }, 750);

  private broadcastRefresh = _.throttle(activeWorklist => {
    this.messageService.broadcast('list.refresh', { worklist: activeWorklist });
  }, 750);

  //try to update to do counts from info we have client side.
  private countUpdates(state, target) {
    if (!this.tryToGetLocalCountUpdate(state, target)) {
      this.tryToGetCountUpdate();
    }
  }

  private getAssignedUserIndex(state) {
    if (
      state.extAppWorkItemState === 'RA1000_Approver_Added' ||
      (!!state.ownership.assignedToUser[0] && state.ownership.assignedToUser[0].externalUser === false)
    ) {
      return 0;
    }
  }

  private todoCountNeedToUpdate(state) {
    let todoCountUpdate = false;
    this.assignedUser = this.getAssignedUserIndex(state);
    if (state.request.actionCode === 'TAKE_CONTROL') {
      todoCountUpdate = true;
    } else if (
      this.assignedUser === undefined &&
      (state.request.actionCode === 'UNASSIGN' ||
        state.request.actionCode === 'READ_CLOSE_VIEWER' ||
        state.request.actionCode === 'READ_CLOSE' ||
        state.request.actionCode === 'READ_CLOSE_AUTOADVANCE_UNASSIGN' ||
        state.request.actionCode === 'READ_UNLOCK')
    ) {
      todoCountUpdate = true;
    } else if (
      this.assignedUser !== undefined &&
      state.request.actionCode !== 'UNASSIGN' &&
      state.request.actionCode !== 'READ_CLOSE_DISCARD'
    ) {
      todoCountUpdate = true;
    } else if (
      state.request.actionCode === 'READ_CLOSE_DISCARD' &&
      (state.request.reportStatus === REPORT_STATUS.DISCARD ||
        state.request.reportStatus === REPORT_STATUS.DISCARD_ADDENDUM)
    ) {
      todoCountUpdate = true;
    } else if (
      state.request.actionCode === Events.savereport.value &&
      state.request.reportStatus === REPORT_STATUS.FINAL
    ) {
      todoCountUpdate = true;
    } else if (
      state.request.actionCode === Events.selectCoReader.value ||
      state.request.actionCode === Events.rejectReport.value
    ) {
      todoCountUpdate = true;
    }
    return todoCountUpdate;
  }

  updateToDoCount(target, state) {
    if (state && state.ownership && state.request) {
      // The owner name checking is skipped for takecontrol event since both the users have to update the count
      // The assignedToUser userName checking is for RA1000 update
      if (this.todoCountNeedToUpdate(state)) {
        this.countUpdates(state, target);
        this.refreshWorklistOnCountUpdate(state);
      }
    }
  }
  refreshWorklistOnCountUpdate(state) {
    const activeWorklist = this.applicationConfigurationService.getActiveList();

    if (activeWorklist && activeWorklist.id) {
      switch (activeWorklist.id) {
        case ToDoListId.ASSIGNED: // Refresh assigned worklist for all the actions
          this.broadcastRefresh(activeWorklist);
          break;
        case ToDoListId.UNSIGNED: // Refresh unsigned and attending read worklist on close action
        case ToDoListId.ATTENDING:
          if (state.request.actionCode === 'READ_CLOSE') {
            this.broadcastRefresh(activeWorklist);
          }
          break;
        case ToDoListId.NAW_REVIEWREQUESTED: // Refresh naw worklists on naw actions.
        case ToDoListId.NAW_COREPORT:
          if (
            state.request.actionCode === Events.selectCoReader.value ||
            state.request.actionCode === Events.rejectReport.value ||
            state.request.actionCode === Events.close.value
          ) {
            this.broadcastRefresh(activeWorklist);
          }
          break;
      }
    }

    this.messageService.broadcast('list.count.update');
  }

  private isMyAttendingOrUnsignedWL(eppUpdate) {
    return (
      this.applicationConfigurationService.getUserId() === eppUpdate.radUsername &&
      (eppUpdate.worklistName === 'attendingread' || eppUpdate.worklistName === 'reportstate')
    );
  }

  private registerEvents() {
    this.messageService.on('WorklistCountChange.UPDATE').subscribe(eppUpdate => {
      // WorklistCountChange event received for Unsigned or Attending Read worklist for current user
      if (this.isMyAttendingOrUnsignedWL(eppUpdate)) {
        this.tryToGetCountUpdate();
        const activeWorklist = this.applicationConfigurationService.getActiveList();

        // Compare the worklist name from event with active worklist
        if (
          (eppUpdate.worklistName === 'attendingread' && activeWorklist.id === 'attendingread') ||
          (eppUpdate.worklistName === 'reportstate' && activeWorklist.id === 'unsigned')
        ) {
          this.broadcastRefresh(activeWorklist);
        }
      }
    });

    this.messageService.on('RMQNotificationMsg.UPDATE').subscribe(eppUpdate => {
      const activeWorklist = this.applicationConfigurationService.getActiveList();
      if (eppUpdate.value === 'failover' && activeWorklist.id !== 'search-list') {
        this.broadcastRefresh(activeWorklist);
      }
    });

    this.messageService.on('UNSIGNED_WORKLIST_REFRESH.REFRESH').subscribe(() => {
      this.tryToGetCountUpdate();
      const activeWorklist = this.applicationConfigurationService.getActiveList();
      if (activeWorklist.id === 'unsigned') {
        this.messageService.broadcast('list.refresh', { worklist: activeWorklist });
      }
    });

    _.each(
      [
        'releaseLock',
        'read',
        'close',
        'closeviewer',
        'autoAdvanceSkipAssign',
        'autoAdvanceSkipUnAssign',
        'closediscard',
        Events.savereport.name,
        Events.rejectReport.name,
        Events.selectCoReader.name
      ],
      event => {
        this.eventService.afterEvent(
          event,
          (target, state) => {
            this.updateToDoCount(target, state);
          },
          'LOCAL'
        );
      }
    );

    _.each([Events.rejectReport.name, Events.selectCoReader.name], event => {
      this.eventService.afterEvent(
        event,
        (target, state) => {
          // we process REMOTE NAW events only if the current user is involved
          if (state?.nawState?.owner?.userName === this.applicationConfigurationService.getUserId()) {
            this.updateToDoCount(target, state);
          }
        },
        'REMOTE'
      );
    });
  }
}
